export type EmailPlatform = "gmail" | "outlook" | "other" | "";
export type FieldServiceManagement =
  | "servicetitan"
  | "housecall-pro"
  | "other"
  | "";

export type CallsCoverage = "24-7" | "custom" | "";
export type LocationScheduleMode = "shared" | "separate" | "";
export type LocationBudgetMode = "flexible" | "fixed" | "";
export type ServiceAreaExclusionMode = "none" | "exclude" | "";
export type MarketingProAnswer = "yes" | "no" | "unsure" | "";

export type MediaSharingMethod =
  | "existing-folder"
  | "hvac-launch-folder"
  | "need-help"
  | "";

export interface ClientLocation {
  id: string;
  name: string;
  address: string;
  licenseNumber: string;
  marketName: string;
  callsCoverage: CallsCoverage;
  callHours: string;
}

export interface ClientContext {
  clientId: string;
  notionPageId?: string;
  brandName: string;
  contactFirstName: string;
  clientEmail: string;
  cpName?: string;
  cpEmail?: string;
  cpNotionUserId?: string;
  website?: string;
  locations: ClientLocation[];
  schedulerUrl?: string;
  expiresAt?: string;
}

export interface LocationAnswers {
  licenseNumber: string;
  marketName: string;
  callsCoverage: CallsCoverage;
  callHours: string;
  serviceZipCodes: string;
  serviceAreaExclusionMode: ServiceAreaExclusionMode;
  excludedZipCodes: string;
}

export interface PersonAnswer {
  name: string;
  email: string;
}

export interface OnboardingAnswers {
  selectedLocationIds: string[];
  locationDetails: Record<string, LocationAnswers>;
  locationBudgetMode: LocationBudgetMode;
  locationScheduleMode: LocationScheduleMode;
  salesRepTitle: string;
  qaRoleTitle: string;
  noQaRole: boolean;
  emailPlatform: EmailPlatform;
  otherEmailPlatform: string;
  fsm: FieldServiceManagement;
  otherFsm: string;
  serviceTitanMarketingPro: MarketingProAnswer;
  sharedInbox: string;
  needsSharedInbox: boolean;
  primaryLeadContact: PersonAnswer;
  officeManager: PersonAnswer;
  noOfficeManager: boolean;
  notificationEmails: string[];
  salespeople: PersonAnswer[];
  salesManager: PersonAnswer;
  noSalesManager: boolean;
  mediaSharingMethod: MediaSharingMethod;
  meetingAcknowledgements: {
    metaAdmin: boolean;
    fsmAccess: boolean;
    delayRisk: boolean;
  };
  submitted: boolean;
  meetingBooked: boolean;
}

export interface OnboardingBootstrap {
  mode: "preview" | "live";
  client: ClientContext;
  answers: OnboardingAnswers;
  currentStep?: string;
  status: "invited" | "in_progress" | "completed";
  booking?: BookingConfirmation;
  revision: number;
  submissionUpdatedAt?: string;
  lastMutationId?: string;
  recoveryWarning?: string;
}

export interface SaveDraftPayload {
  answers: OnboardingAnswers;
  currentStep: string;
}

export interface VersionedSaveDraftPayload extends SaveDraftPayload {
  expectedRevision: number;
  mutationId: string;
}

export interface DraftSaveResult {
  revision: number;
  updatedAt: string;
}

export interface CompletionResult extends DraftSaveResult {
  schedulerUrl?: string;
}

export interface SchedulingSlot {
  startTime: string;
  status: "available";
}

export interface SchedulingAvailability {
  slots: SchedulingSlot[];
  timezone: string;
  preview: boolean;
}

export interface BookingConfirmation {
  startTime: string;
  timezone: string;
  cancelUrl?: string;
  rescheduleUrl?: string;
}

export interface FormFeedback {
  rating: number;
  feedback: string;
}

export interface FlowStep {
  id: string;
  section: string;
  label: string;
  chapter: ChapterId;
}

export type ChapterId =
  | "quick-start"
  | "service-area"
  | "team-systems"
  | "lead-follow-up"
  | "creative-library"
  | "call-prep";

export interface FlowChapter {
  id: ChapterId;
  number: number;
  label: string;
}

export function createEmptyAnswers(client: ClientContext): OnboardingAnswers {
  return {
    selectedLocationIds:
      client.locations.length === 1 ? [client.locations[0].id] : [],
    locationDetails: Object.fromEntries(
      client.locations.map((location) => [
        location.id,
        {
          licenseNumber: location.licenseNumber,
          marketName: location.marketName,
          callsCoverage: location.callsCoverage,
          callHours: location.callHours,
          serviceZipCodes: "",
          serviceAreaExclusionMode: "",
          excludedZipCodes: "",
        },
      ]),
    ),
    locationBudgetMode: "",
    locationScheduleMode: "",
    salesRepTitle: "",
    qaRoleTitle: "",
    noQaRole: false,
    emailPlatform: "",
    otherEmailPlatform: "",
    fsm: "",
    otherFsm: "",
    serviceTitanMarketingPro: "",
    sharedInbox: "",
    needsSharedInbox: false,
    primaryLeadContact: { name: "", email: "" },
    officeManager: { name: "", email: "" },
    noOfficeManager: false,
    notificationEmails: [],
    salespeople: [],
    salesManager: { name: "", email: "" },
    noSalesManager: false,
    mediaSharingMethod: "",
    meetingAcknowledgements: {
      metaAdmin: false,
      fsmAccess: false,
      delayRisk: false,
    },
    submitted: false,
    meetingBooked: false,
  };
}
